import { DateTime } from 'luxon';
import mapboxgl from 'mapbox-gl';
import type { MapGeoJSONFeature } from 'react-map-gl/mapbox';
import type { TOCItem, TOCParent, TOC as TOCType } from '~/common/types';
import { CG_ORG_ID, CG_PROJ_ID, REPORT_ALL_ID } from '~/common/types';
import { CUSTOM_DATA_LAYER_PREFIX } from '~/pages/projectDetails/map/customDataLayers.helpers';
import { humanizeCateTitle } from '~/pages/projectDetails/map/customDataSymbology';
import type { MapboxSourceConfig } from '~/store2/layers.data';

// A `buffer`-px square around a screen point, as the [sw, ne] corner pair queryRenderedFeatures expects.
export function calcBoundingBox(point: mapboxgl.Point, buffer: number): [mapboxgl.Point, mapboxgl.Point] {
  return [
    mapboxgl.Point.convert([point.x - buffer, point.y - buffer]),
    mapboxgl.Point.convert([point.x + buffer, point.y + buffer]),
  ];
}

// Fields the metadata sidebar never shows.
export const EXCLUDED_FIELDS: string[] = [CG_PROJ_ID, CG_ORG_ID, REPORT_ALL_ID];

export function isParcelFeature(feature: MapGeoJSONFeature): boolean {
  return feature.layer?.id === 'landRightsParcels-all' || feature.layer?.source === 'landRightsParcels';
}

type RawParcelData = { [key: string]: any };

interface Fields {
  alias: string;
  type: 'string' | 'id' | 'date';
}

interface FormattedParcelData {
  [key: string]: {
    fields: Fields;
    value: any;
  };
}

export function formatParcelData(data: RawParcelData[]) {
  const parcels: FormattedParcelData[] = [];
  const attributes = Array.from(attributeMap.keys());

  data.forEach((parcel, i: number) => {
    const parcelProps: string[] = Object.keys(parcel) || [];
    const formattedParcel: { [key: string]: any } = {};

    attributes.forEach((attr: string, i: number) => {
      if (parcelProps.includes(attr)) {
        formattedParcel[attr] = parcel[attr];
      }
    });

    parcels.push(formattedParcel);
  });

  return parcels;
}

export const ZIP_FIELD_KEYS = new Set(['physzip', 'census_zip', 'mail_zipcode']);

export function normalizeZip(value: unknown): string {
  if (value == null || value === '') return String(value ?? '');
  return String(value).padStart(5, '0');
}

export function formatParcelField(field: string): MapboxSourceConfig['fields'][0] {
  const formattedField = attributeMap.get(field);

  return {
    alias: formattedField ? formattedField : field,
    type: 'string',
  };
}

// Link to all Reportall attributes:
// https://reportallusa.com/solutions/overlay/api-standard-data-dictionary
export const attributeMap: Map<string, string> = new Map([
  ['parcel_id', 'Parcel ID'],
  ['owner', 'Owner Name'],
  ['mail_name', 'Mailing Name'],
  ['muni_name', 'Municipality'],
  ['county_name', 'County'],
  ['county_id', 'County ID'],
  ['state_abbr', 'State Abbreviation'],
  ['addr_number', 'Address'],
  ['addr_street_prefix', 'Site Street Prefix'],
  ['addr_street_name', 'Site Street Name'],
  ['addr_street_suffix', 'Site Street Suffix'],
  ['addr_street_type', 'Site Street Type'],
  ['physcity', 'Site City'],
  ['physzip', 'Site ZIP Code'],
  ['census_zip', 'Census ZIP Code'],
  ['usps_residential', 'USPS Classification'],
  ['mail_address1', 'House No., Street Name/Type, or PO Box'],
  ['mail_address2', 'Suite No., Bldg No., Other Info'],
  ['mail_address3', 'City, State, ZIP'],
  ['mail_recipient', 'Mailing Recipient'],
  ['mail_uspsbox', 'Mailing Address USPS Box'],
  ['mail_addressnumber', 'Mailing Address Street Number'],
  ['mail_streetnamepre', 'Mailing Address Street Name'],
  ['mail_streetnameposttype', 'Mailing Address Street Name Post Type'],
  ['mail_streetnamepostdirectional', 'Mailing Address Street Name Post Directional'],
  ['mail_subaddressoccupancy', 'Mailing Address Subaddress Occupancy'],
  ['mail_placename', 'Mailing Address Place Name'],
  ['mail_statename', 'Mailing Address State Name'],
  ['mail_zipcode', 'Mailing Address ZIP Code'],
  ['trans_date', 'Most Recent Transfer Date'],
  ['sale_price', 'Sale Price'],
  ['mkt_val_land', 'Land Market Value'],
  ['mkt_val_bldg', 'Building Market Value'],
  ['mkt_val_tot', 'Total Market Value'],
  ['bldg_sqft', 'Building Area (sf)'],
  ['year_built', 'Year Built'],
  ['ngh_code', 'Neighborhood Code'],
  ['land_use_code', 'Land Use Code'],
  ['land_use_class', 'Derived Land Use Class'],
  ['story_height', 'Story Height'],
  ['muni_id', 'Census Municipality ID'],
  ['school_dist_id', 'Census School District ID'],
  ['acreage', 'Deeded Acreage'],
  ['acreage_calc', 'Calculated Acreage'],
  ['latitude', 'Latitude'],
  ['longitude', 'Longitude'],
  ['acreage_adjacent_with_sameowner', 'Nearby Acreage With Same Owner'],
  ['owner_occupied', 'Owner Occupied'],
  ['elevation', 'Elevation (ft)'],
  ['buildings', 'Buildings'],
  ['legal_desc1', 'Legal Description 1'],
  ['legal_desc2', 'Legal Description 2'],
  ['legal_desc3', 'Legal Description 3'],
  ['last_updated', 'Last Updated (YYYY-QQ)'],

  ['alt_id_1', 'Alternate ID 1'],
  ['alt_id_2', 'Alternate ID 2'],
  ['section_township_range', 'Section, Township, Range (From PLSS)'],
  ['land_cover', 'Land Cover'],
  ['crop_cover', 'Crop Cover'],
  // ['geom_as_wkt', 'Parcel Geometry'],
  ['rausa_id', 'Reportall USA ID'],
  ['robust_id', 'Robust ID'],
]);

// Minimal feature shape the inspect helpers need — kept narrow so they stay
// unit-testable without a live Mapbox feature.
export interface CustomDataFeatureLike {
  layer?: { id?: string; source?: string } | null;
  source?: string;
}

// True when a queried feature belongs to a custom-data vector line layer
// (id/source prefixed with `customData:`). Custom-data lines carry only `CateTitle`
// and have no tilesConfig entry, so the metadata sidebar renders them via a dedicated branch.
export function isCustomDataFeature(feature: CustomDataFeatureLike | null | undefined): boolean {
  if (!feature) return false;
  const ids = [feature.layer?.id, feature.layer?.source, feature.source];
  return ids.some((id) => typeof id === 'string' && id.startsWith(CUSTOM_DATA_LAYER_PREFIX));
}

// The custom-data Mapbox layer id of a feature. Fill/circle/line sublayers share one source
// whose id is the BASE layer id, while feature.layer.id may be a `-fill`/`-circle` sublayer —
// so prefer the source id. Empty string when it isn't a custom-data feature.
export function customDataFeatureLayerId(feature: CustomDataFeatureLike | null | undefined): string {
  if (!feature) return '';
  const ids = [feature.layer?.source, feature.source, feature.layer?.id];
  return ids.find((id) => typeof id === 'string' && id.startsWith(CUSTOM_DATA_LAYER_PREFIX)) ?? '';
}

// Friendly label for a custom-data layer id, sourced from the "Custom Data" TOC group
// children the app already stores. Falls back to "Custom Data" when not found.
export function customDataLayerLabel(toc: TOCType | undefined, layerId: string): string {
  const fallback = 'Custom Data';
  if (!toc || !layerId) return fallback;
  let label = fallback;
  let found = false;
  const isParent = (item: TOCItem): item is TOCParent => 'children' in item && Array.isArray(item.children);
  const walk = (items: TOCItem[]) => {
    for (const item of items) {
      if (found) return;
      if (isParent(item)) {
        // A split upload (2+ categories) is a TOCParent carrying the row's layer id on
        // sectionId; its leaves are per-category ids that never equal the bare row id.
        if (item.sectionId === layerId) {
          label = item.label;
          found = true;
          return;
        }
        walk(item.children);
      } else if (item.id === layerId) {
        label = item.label;
        found = true;
        return;
      }
    }
  };
  walk(toc);
  return label;
}

export function obfuscateValue(v: string) {
  let out = '';
  for (let i = 0; i < v.length; i++) {
    if (v[i] === ' ') {
      out += ' ';
    } else {
      out += v[Math.floor(Math.random() * v.length)];
    }
  }
  return out;
}

// Formats a single feature field for display: id-type fields are relabeled "ID", date-type values
// are rendered as "MMM dd, yyyy", and only plain string values with an explicit http(s) scheme are
// flagged as urls (id/date values are never treated as links).
export function formatFieldRow(field: Fields, rawValue: unknown): { label: string; value: string; isUrl: boolean } {
  const label = field.type === 'id' ? 'ID' : field.alias;
  if (field.type === 'date') {
    // Formats the RAW value unconditionally so numeric epoch-ms (live Esri path) is preserved.
    const value = DateTime.fromJSDate(new Date(rawValue as string | number)).toFormat('MMM dd, yyyy');
    return { label, value, isUrl: false };
  }
  const value = typeof rawValue === 'boolean' ? '' : String(rawValue ?? '');
  return { label, value, isUrl: field.type === 'string' && /^https?:\/\//i.test(value) };
}

// Derives the section label shown above a feature's fields in the metadata sidebar.
// Custom-data features have no legend parent, so their category comes from CateTitle
// (humanized). Utility/other layers use the TOC legend's "Section / Leaf" parent string,
// keeping only the section (before " / "); layers with no legend parent (e.g. parcels)
// render no category.
export function deriveCategoryLabel(args: {
  layerId: string;
  isCustomData: boolean;
  cateTitle?: string;
  legendParentMap: Map<string, string>;
}): string | null {
  const { layerId, isCustomData, cateTitle, legendParentMap } = args;
  if (isCustomData) {
    if (!cateTitle) return null;
    return humanizeCateTitle(cateTitle);
  }
  const parent = legendParentMap.get(layerId); // "Section / Leaf"
  if (!parent) return null;
  return parent.split(' / ')[0] ?? null;
}
